/* ============================================================================
   Studio brand — the studio's own logo, set once in the Account admin and used
   as the letterhead mark on every report and every project that hasn't uploaded
   its own studio logo. Synced to D1 as one "meta" aggregate (id "studio-brand")
   so the mark reaches every device. This is a plain client-written meta row —
   NOT the server-locked "studio-settings" row — so it round-trips normally.
   ========================================================================== */
import { useEffect, useState } from "react";

const KEY = "atelier.studiobrand.v1";
export const STUDIO_BRAND_AGG_ID = "studio-brand";

type State = { logo?: string; logoAt?: string };

function load(): State {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return {};
}

let state: State = load();
const subs = new Set<() => void>();

function commit(next: State) {
  state = next;
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* quota */ }
  subs.forEach((f) => f());
  changeListener?.(asAggregate());
}

export function useStudioBrand(): State {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    subs.add(f);
    return () => { subs.delete(f); };
  }, []);
  return state;
}
/** The studio logo data-URI, if one has been set. Safe to call outside React. */
export function getStudioLogo(): string | undefined { return state.logo; }

/* --- sync hooks --------------------------------------------------------- */
export type StudioBrandAggregate = { id: string; logo?: string; logoAt?: string };
function asAggregate(): StudioBrandAggregate { return { id: STUDIO_BRAND_AGG_ID, logo: state.logo, logoAt: state.logoAt }; }
let changeListener: ((a: StudioBrandAggregate) => void) | null = null;
export function onStudioBrandChange(f: (a: StudioBrandAggregate) => void) { changeListener = f; }
export function getStudioBrandAggregates(): StudioBrandAggregate[] { return [asAggregate()]; }
export function replaceStudioBrandAggregates(aggs: { id: string }[]) {
  const row = aggs.find((a) => a.id === STUDIO_BRAND_AGG_ID) as StudioBrandAggregate | undefined;
  if (!row) return;
  state = { logo: row.logo, logoAt: row.logoAt };
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* quota */ }
  subs.forEach((f) => f());
}

/* --- actions ------------------------------------------------------------ */
export const studioBrandActions = {
  setLogo(dataUri: string | undefined) {
    commit(dataUri ? { logo: dataUri, logoAt: new Date().toISOString() } : {});
  },
};
